import React, {
  WheelEvent,
  SVGAttributes,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  ComposedChart,
  Brush,
  Area,
} from 'recharts';
import ForecastLineChartXAxisTick from './ForecastLineChartXAxisTick';
import ForecastLineChartYAxisTick from './ForecastLineChartYAxisTick';
import { ActiveShape } from 'recharts/types/util/types';
import { TooltipProps } from 'recharts/types/component/Tooltip';
import { Props as LegendContentProps } from 'recharts/types/component/DefaultLegendContent';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import SVGImage from '../layout/SVGImage';
import useTailwindConfig from '../../hooks/useTailwindConfigColor';
import md5 from 'md5';

interface ForecastLineChartProps {
  data: Array<ForecastLineChartData>;
  showLegend?: boolean;
  className?: string;
}

type ForecastLineChartData = {
  name: string;
  [ForecastMeasurementType.Measurement]?: number | null;
  [ForecastMeasurementType.FirstPrediction]?: number | null;
  [ForecastMeasurementType.SecondPrediction]?: number | null;
  [ForecastMeasurementType.PredictionErrorRate]?: number | null;
  [ForecastMeasurementType.SettlementUnitPrice]?: number | null;
};

type RADType = {
  cx: number;
  cy: number;
  dataKey: string;
};

type TravellerProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  stroke?: SVGAttributes<SVGElement>['stroke'];
};

export enum ForecastMeasurementType {
  FirstPrediction = 'firstPrediction',
  SecondPrediction = 'secondPrediction',
  Measurement = 'measurement',
  PredictionErrorRate = 'predictionErrorRate',
  SettlementUnitPrice = 'settlementUnitPrice',
}

export enum ForecastAreaMeasurementType {
  FirstSettlementUnitPrice = 'firstSettlementUnitPrice',
  SecondSettlementUnitPrice = 'secondSettlementUnitPrice',
}

const ForecastLineChart: React.FC<ForecastLineChartProps> = ({
  data,
  showLegend,
  className,
}) => {
  const { t } = useTranslation();
  // 주어진 데이터를 기반으로 dataKey를 가져오는 작업
  const {
    theme: { colors },
  } = useTailwindConfig();
  const ForecastMeasurementTypeValues = useCallback(
    (
      type: typeof ForecastMeasurementType | typeof ForecastAreaMeasurementType
    ) => Object.values(type),
    []
  );
  const makeDataKeys = useCallback(
    (
      type: typeof ForecastMeasurementType | typeof ForecastAreaMeasurementType
    ) => {
      const dataKeys = Object.keys(data[0]).filter((key) =>
        ForecastMeasurementTypeValues(type).includes(key as string)
      );
      return dataKeys;
    },
    [data, ForecastMeasurementTypeValues]
  );
  const [dataKeys, setDataKeys] = useState(
    makeDataKeys(ForecastMeasurementType)
  );
  const [areaDataKeys, setAreaDataKeys] = useState(
    makeDataKeys(ForecastAreaMeasurementType)
  );
  // dataKey toggle value map for handling active line chart
  const [dataKeyToggle, setDataKeyToggle] = useState<{
    [key: string]: boolean;
  }>(dataKeys.reduce((p, c) => ({ [c]: true, ...p }), {}));
  // 현재 Traveller 위치를 기반으로 Brush 위치를 설정
  const [brushY, setBrushY] = useState<number | undefined>(undefined);
  // Brush Index
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(data.length - 1);
  const chartRef = useRef<HTMLDivElement>(null);

  const handleWheel = debounce((event: WheelEvent) => {
    const zoomFactor = 1;
    const currentRange = endIndex - startIndex;

    if (event.deltaY < 0) {
      // Scroll up (Zoom In)
      if (currentRange > 2) {
        setStartIndex((prev) => Math.min(prev + zoomFactor, data.length - 2));
        setEndIndex((prev) => Math.max(prev - zoomFactor, 1));
      }
    } else {
      // Scroll down (Zoom Out)
      setStartIndex((prev) => Math.max(prev - zoomFactor, 0));
      setEndIndex((prev) => Math.min(prev + zoomFactor, data.length - 1));
    }
  }, 50);

  useEffect(() => {
    const chartElement = chartRef.current;

    if (chartElement) {
      // Add event listener with passive: false to allow event.preventDefault()
      chartElement.addEventListener(
        'wheel',
        (e: globalThis.WheelEvent) => e.preventDefault(),
        {
          passive: false,
        }
      );

      // Cleanup the event listener when component unmounts
      return () => {
        chartElement.removeEventListener('wheel', (e: globalThis.WheelEvent) =>
          e.preventDefault()
        );
      };
    }
  }, []);

  useEffect(() => {
    const dataKey = makeDataKeys(ForecastMeasurementType);
    setStartIndex(0);
    setEndIndex(data.length - 1);
    setDataKeys(dataKey);
    setAreaDataKeys(makeDataKeys(ForecastAreaMeasurementType));
    setDataKeyToggle(dataKey.reduce((p, c) => ({ [c]: true, ...p }), {}));
  }, [data]);

  const sortedDataKeys = (
    data: Array<string>,
    dataKeyToggle: { [key: string]: boolean }
  ): Array<string> => {
    return data.sort((a, b) => {
      const aValue = dataKeyToggle[a];
      const bValue = dataKeyToggle[b];

      if (aValue === bValue) {
        return 0;
      }

      return aValue ? 1 : -1;
    });
  };

  const getColor = (
    dataKey: string,
    options?: {
      isLight?: boolean;
    }
  ): string => {
    switch (dataKey) {
      case ForecastMeasurementType.Measurement:
        return colors.teal.light;
      case ForecastMeasurementType.FirstPrediction:
        return options?.isLight ? colors.yellow.light : colors.yellow.main;
      case ForecastMeasurementType.SecondPrediction:
        return colors.orange.light;
      case ForecastMeasurementType.PredictionErrorRate:
        return colors.red.light;
      case ForecastMeasurementType.SettlementUnitPrice:
        return colors.blue.light;
      default:
        return 'black';
    }
  };

  const getTextColor = (dataKey: string): string => {
    switch (dataKey) {
      case ForecastMeasurementType.Measurement:
        return 'text-teal-light';
      case ForecastMeasurementType.FirstPrediction:
        return 'text-yellow-main';
      case ForecastMeasurementType.SecondPrediction:
        return 'text-orange-main';
      case ForecastMeasurementType.PredictionErrorRate:
        return 'text-red-light';
      case ForecastMeasurementType.SettlementUnitPrice:
        return 'text-blue-light';
      default:
        return 'black';
    }
  };

  const getHoverDropShadowClass = (dataKey: string): string => {
    switch (dataKey) {
      case ForecastMeasurementType.Measurement:
        return '[&:hover>path]:drop-shadow-teal';
      case ForecastMeasurementType.FirstPrediction:
        return '[&:hover>path]:drop-shadow-yellow';
      case ForecastMeasurementType.SecondPrediction:
        return '[&:hover>path]:drop-shadow-orange';
      case ForecastMeasurementType.PredictionErrorRate:
        return '[&:hover>path]:drop-shadow-red';
      case ForecastMeasurementType.SettlementUnitPrice:
        return '[&:hover>path]:drop-shadow-blue';
      default:
        return '';
    }
  };

  const isActiveDot = (dataKey: string | undefined | number): boolean => {
    if (!dataKey) return false;
    return dataKeyToggle[dataKey];
  };

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<string, string>) => {
    if (active && payload && payload.length) {
      const activeDot = payload.filter(({ dataKey }) => isActiveDot(dataKey));
      // Multiple active lines are selected/visible
      if (activeDot.length > 1) {
        return (
          <div className="noto-normal-3 flex flex-col items-center">
            <div className="w-[185px] rounded-[6px] bg-white px-[12px] pb-[14px] pt-[12px] shadow-5">
              <span className="leading-none text-dark-4">{label}</span>
              <div className="noto-normal-3">
                {activeDot.map(({ dataKey, value }, index) =>
                  dataKey === undefined || typeof dataKey === 'number' ? (
                    <></>
                  ) : (
                    <div
                      key={index}
                      className="mr-[43px] mt-[14px] flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <SVGImage
                          name="tooltip-icon"
                          className="mr-[6px]"
                          width={10}
                          height={10}
                          fill={getColor(dataKey)}
                        />
                        <p className="noto-semibold-3 inline-block leading-none text-dark-3">
                          {t(`forecast.${dataKey}`)}
                        </p>
                      </div>

                      <span
                        className={`${getTextColor(dataKey)} noto-13-semibold leading-none underline underline-offset-2px`}
                      >
                        {Number(value).toFixed(1)}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        );
        // One active line is selected/visible
      } else {
        const dataKey = activeDot[0]?.dataKey;
        // return null if dataKey is not exist
        if (dataKey === undefined || typeof dataKey === 'number') return null;

        const value = activeDot[0]?.value;
        const color = getColor(dataKey);
        const textColor = getTextColor(dataKey);

        // when Area Chart included
        if (areaDataKeys.length > 0) {
          return (
            <div className="flex flex-col items-center">
              <div className="rounded-[6px] bg-white px-[15px] py-[4px] shadow-5">
                <div className="flex items-center">
                  <SVGImage
                    name="tooltip-icon"
                    className="mr-[5px]"
                    fill={color}
                  />
                  <p className="noto-bold-3 leading-[22px] text-dark-3">
                    {t(`forecast.${dataKey}`)}
                  </p>
                </div>
                <div className="noto-normal-3">
                  <span className="leading-[22px] text-dark-4">{label}</span>
                  <span
                    className={`${textColor} noto-13-bold ml-4 leading-[22px] underline underline-offset-2px`}
                  >
                    {Number(value).toFixed(1)}
                  </span>
                </div>
                <div className="mt-[4px] flex items-center">
                  <SVGImage
                    name="tooltip-icon"
                    className="mr-[5px]"
                    fill={'#00D087'}
                  />
                  <p className="noto-bold-3 leading-[22px] text-dark-3">
                    {t(`forecast.settlementAmount`, { context: 'short' })}
                  </p>
                </div>
                <div className="noto-normal-3">
                  <span className="leading-[22px] text-dark-4">
                    {'4~6% 정산단가'}
                  </span>
                  <span
                    className={`noto-13-bold ml-4 leading-[22px] text-[#00D087] underline underline-offset-2px`}
                  >
                    {'3원/kWh'}
                  </span>
                </div>
                <div className="noto-normal-3">
                  <span className="leading-[22px] text-dark-4">
                    {'0~4% 정산단가'}
                  </span>
                  <span
                    className={`noto-13-bold ml-4 leading-[22px] text-[#00D087] underline underline-offset-2px`}
                  >
                    {'4원/kWh'}
                  </span>
                </div>
              </div>
            </div>
          );
          // Multiple active lines are selected/visible
        } else {
          return (
            <div className="flex flex-col items-center">
              <div className="rounded-[6px] bg-white px-[15px] py-[4px] shadow-5">
                <div className="flex items-center">
                  <SVGImage
                    name="tooltip-icon"
                    className="mr-[5px]"
                    fill={color}
                  />
                  <p className="noto-bold-3 leading-[22px] text-dark-3">
                    {t(`forecast.${dataKey}`)}
                  </p>
                </div>
                <div className="noto-normal-3">
                  <span className="leading-[22px] text-dark-4">{label}</span>
                  <span
                    className={`${textColor} noto-13-bold ml-4 leading-[22px] underline underline-offset-2px`}
                  >
                    {Number(value).toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
          );
        }
      }
    }

    return null;
  };

  const CustomLegend = (props: LegendContentProps) => {
    const { payload } = props;

    return (
      <ul className="mb-[40px] ml-[10px] flex items-center p-0">
        {payload
          ?.filter((entry) =>
            data.some((value) => Object.keys(value).includes(entry.value))
          )
          .sort(
            (a, b) =>
              ForecastMeasurementTypeValues(ForecastMeasurementType).indexOf(
                a.value
              ) -
              ForecastMeasurementTypeValues(ForecastMeasurementType).indexOf(
                b.value
              )
          )
          .map((entry, index) => {
            const dataKey = entry.value;
            const textStyle = dataKeyToggle[dataKey]
              ? 'noto-bold-3'
              : 'noto-normal-3';
            const textColor = dataKeyToggle[dataKey]
              ? 'text-dark-2'
              : 'text-dark-9';

            return (
              <li
                key={index}
                onClick={() => toggleLine(dataKey)}
                className="flex items-center"
              >
                {dataKeyToggle[dataKey] ? (
                  <SVGImage
                    name="tooltip-icon"
                    width={13}
                    height={13}
                    fill={getColor(dataKey)}
                  />
                ) : (
                  <SVGImage
                    name="tooltip-disabled-icon"
                    width={13}
                    height={13}
                    stroke={getColor(dataKey)}
                  />
                )}
                <span
                  className={`${textColor} ${textStyle} ml-[7px] mr-[19px]`}
                >
                  {t(`forecast.${entry.value}`)}
                </span>
              </li>
            );
          })}
      </ul>
    );
  };

  const PlaceholderLegend = () => {
    return <div className="invisible h-[35px] p-0"></div>;
  };

  const renderActiveDot = ({ cx, cy, dataKey }: RADType) => {
    const color = getColor(dataKey, { isLight: true });

    if (isActiveDot(dataKey)) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={7}
          fill={color}
          stroke="white"
          strokeWidth={3}
          style={{
            filter: 'drop-shadow(0px 3px 12px rgba(0, 0, 0, 0.25))',
          }}
        />
      );
    } else {
      return <></>;
    }
  };

  const toggleLine: <P, E>(
    measurementType: ForecastMeasurementType,
    props?: P,
    event?: React.MouseEvent<E, MouseEvent>
  ) => void = (measurementType) => {
    const updateObject = {
      ...dataKeyToggle,
      [measurementType]: !dataKeyToggle[measurementType],
    };
    setDataKeyToggle(updateObject);
    // Sort dataKeys based on the toggle value for z-index purpose
    setDataKeys(sortedDataKeys(dataKeys, updateObject));
  };

  // Brush related constant value
  const brushWidth = 1038;
  const brushX = 30;
  const travellerWidth = 6;

  const CustomTraveller = (props: TravellerProps) => {
    const { x, y, width, height } = props;
    const lineY = Math.floor(y + height / 2) - 1;

    return (
      <>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={colors.dark[4]}
          stroke="none"
          opacity={0.4}
        />
        <line
          x1={x + 2}
          y1={lineY}
          x2={x + width - 2}
          y2={lineY}
          fill="none"
          stroke={colors.light[2]}
        />
        <line
          x1={x + 2}
          y1={lineY + 2}
          x2={x + width - 2}
          y2={lineY + 2}
          fill="none"
          stroke={colors.light[2]}
        />
      </>
    );
  };

  const brushClassName = `[&>g.recharts-brush-traveller:focus>rect]:fill-blue-dark [&>g.recharts-brush-traveller:focus>rect]:[height:44px] [&>g.recharts-brush-traveller:focus>rect]:[opacity:0.7] [&>g.recharts-brush-traveller:focus>rect]:[transform:translateY(-2px)] [&>g.recharts-brush-traveller:focus]:outline-none [&>rect.recharts-brush-slide]:fill-dark-10 [&>rect.recharts-brush-slide]:[fill-opacity:0.15] [&>rect:first-child]:[rx:5] `;
  const brushKey = useMemo(() => md5(JSON.stringify(data)), [data]);

  return (
    <div ref={chartRef} onWheel={handleWheel} className="h-full w-full">
      <ResponsiveContainer
        width="100%"
        height="100%"
        className={`${className}`}
      >
        <ComposedChart data={data} margin={{ left: 20, right: 33, bottom: 30 }}>
          <CartesianGrid
            stroke={colors.light[6]}
            strokeDasharray="3 6"
            vertical={false}
          />
          <Legend
            layout="horizontal"
            align="left"
            verticalAlign="top"
            content={
              showLegend
                ? (props) => CustomLegend(props)
                : () => PlaceholderLegend()
            }
          />
          <Brush
            key={brushKey}
            travellerWidth={travellerWidth}
            stroke={colors.light[3]}
            width={brushWidth}
            x={brushX}
            y={brushY === undefined ? undefined : brushY + 25}
            startIndex={startIndex}
            endIndex={endIndex}
            className={`${brushClassName}`}
            tickFormatter={() => ''}
            traveller={(props: TravellerProps) => {
              if (brushY === undefined) setBrushY(props.y);
              return <CustomTraveller {...props} />;
            }}
          >
            <ComposedChart>
              {areaDataKeys.map((key, index) => (
                <Area
                  type="monotone"
                  key={'area' + index.toString()}
                  dataKey={key}
                  stroke={'none'}
                  fill={
                    key === ForecastAreaMeasurementType.FirstSettlementUnitPrice
                      ? '#4EF2B8'
                      : '#AEFF95'
                  }
                />
              ))}
              {dataKeys.map((key, index) => (
                <Line
                  key={index}
                  type="linear"
                  activeDot={renderActiveDot as ActiveShape}
                  dataKey={key}
                  dot={false}
                  strokeWidth={1}
                  stroke={
                    dataKeyToggle[key]
                      ? getColor(key, { isLight: true })
                      : // toggle to transparent color
                        '#00000000'
                  }
                />
              ))}
            </ComposedChart>
          </Brush>
          <XAxis
            xAxisId={'line'}
            tick={<ForecastLineChartXAxisTick />}
            tickLine={false}
            tickCount={8}
            padding={{ left: 30, right: 30 }}
            dataKey="name"
            axisLine={{ stroke: colors.light[6], width: 1 }}
          />
          <XAxis
            xAxisId={'area'}
            tick={<ForecastLineChartXAxisTick />}
            tickLine={false}
            tickCount={8}
            dataKey="name"
            axisLine={{ stroke: colors.light[6], width: 1 }}
            hide={true}
          />
          <YAxis
            tickMargin={15}
            tickCount={6}
            tickLine={false}
            axisLine={false}
            tick={<ForecastLineChartYAxisTick />}
          />

          <Tooltip cursor={false} content={CustomTooltip} />
          {areaDataKeys.map((key, index) => (
            <Area
              xAxisId={'area'}
              type="monotone"
              key={'area' + index.toString()}
              dataKey={key}
              activeDot={renderActiveDot}
              stroke={'none'}
              fill={
                key === ForecastAreaMeasurementType.FirstSettlementUnitPrice
                  ? '#4EF2B8'
                  : '#AEFF95'
              }
            />
          ))}
          {dataKeys.map((key, index) => (
            <Line
              xAxisId={'line'}
              key={index}
              type="linear"
              activeDot={renderActiveDot as ActiveShape}
              onClick={(props, event) =>
                toggleLine(key as ForecastMeasurementType, props, event)
              }
              className={`${getHoverDropShadowClass(key)} [&:hover>path]:filter`}
              dataKey={key}
              dot={{ r: 1 }}
              strokeWidth={2}
              stroke={
                dataKeyToggle[key]
                  ? getColor(key, { isLight: true })
                  : '#E8EEEE'
              }
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ForecastLineChart;
